import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import { message, Pagination, Dropdown, Modal, Input } from "antd";
import {
  Ban,
  CheckCircle2,
  ShieldCheck,
  ShieldOff,
  Slash,
  Unlock,
  UserCheck,
  UserX,
  MoreVertical,
  Search,
  RefreshCw,
  Users,
} from "lucide-react";
import verifiedBadge from "../../../assets/verified.png";
import communityAPI from "../../../api/community";
import { useDisclosure } from "../../../hooks/useDisclosure";

const { Search: SearchInput, TextArea } = Input;

const SUSPEND_PRESETS = [
  { label: "24 hours", value: 24 },
  { label: "3 days", value: 72 },
  { label: "7 days", value: 168 },
  { label: "30 days", value: 720 },
  { label: "Custom date", value: "custom" },
];

const badgeBaseClass =
  "inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold tracking-wide";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }
  return date.toLocaleString();
};

const extractResults = (payload) =>
  Array.isArray(payload) ? payload : payload?.results || [];

const filterModeratedMembers = (records = []) =>
  records.filter(
    (entry) => !(entry?.is_superuser || entry?.is_staff || entry?.role === "admin")
  );

const buildSummary = (records = []) =>
  records.reduce(
    (acc, user) => {
      const status = user?.status || {};
      acc.totalMembers += 1;
      if (status.is_banned) acc.banned += 1;
      if (status.currently_suspended) acc.suspended += 1;
      if (status.is_trusted) acc.trusted += 1;
      return acc;
    },
    { totalMembers: 0, banned: 0, suspended: 0, trusted: 0 }
  );

const renderActionLabel = (key, label, options = {}) => {
  const baseClasses =
    "block w-full rounded-md px-2 py-1 text-xs font-medium";

  let bgClasses = "bg-slate-50 text-gray-800";

  switch (key) {
    case "suspend":
      bgClasses = "bg-amber-50 text-amber-700";
      break;
    case "ban":
      bgClasses = "bg-rose-50 text-rose-700";
      break;
    case "toggle-posting":
      bgClasses = options.enabled
        ? "bg-slate-50 text-slate-700" // Disable Posting
        : "bg-emerald-50 text-emerald-700"; // Enable Posting
      break;
    case "toggle-commenting":
      bgClasses = options.enabled
        ? "bg-slate-50 text-slate-700" // Disable Commenting
        : "bg-emerald-50 text-emerald-700"; // Enable Commenting
      break;
    case "toggle-trust":
      bgClasses = options.trusted
        ? "bg-rose-50 text-rose-700" // Unmark Trusted
        : "bg-emerald-50 text-emerald-700"; // Mark as Trusted
      break;
    default:
      break;
  }

  return <span className={`${baseClasses} ${bgClasses}`}>{label}</span>;
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isLoading,
  danger,
  collectReason,
  reasonValue,
  onReasonChange,
  reasonPlaceholder,
}) => (
  <Modal
    open={isOpen}
    onCancel={onClose}
    title={
      <div className="flex items-center gap-2">
        <Ban className={`h-5 w-5 ${danger ? "text-rose-500" : "text-amber-500"}`} />
        <span className="text-lg font-semibold">{title}</span>
      </div>
    }
    footer={[
      <button key="back" onClick={onClose} disabled={isLoading} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
        {cancelText}
      </button>,
      <button
        key="submit"
        type="button"
        disabled={isLoading}
        onClick={onConfirm}
        className={`ml-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
          danger
            ? "bg-rose-600 hover:bg-rose-700"
            : "bg-primary hover:bg-primary/90"
        }`}
      >
        {isLoading ? "Processing..." : confirmText}
      </button>,
    ]}
  >
    <div className="space-y-3">
      <p className="text-gray-600 dark:text-gray-400">{message}</p>
      {collectReason && (
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Moderation Reason (optional)
          </label>
          <TextArea
            value={reasonValue}
            onChange={(event) => onReasonChange?.(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder={reasonPlaceholder || "Provide context for the user notification"}
          />
        </div>
      )}
    </div>
  </Modal>
);

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  isLoading: PropTypes.bool,
  danger: PropTypes.bool,
  collectReason: PropTypes.bool,
  reasonValue: PropTypes.string,
  onReasonChange: PropTypes.func,
  reasonPlaceholder: PropTypes.string,
};

ConfirmationModal.defaultProps = {
  confirmText: "Confirm",
  cancelText: "Cancel",
  isLoading: false,
  danger: false,
  collectReason: false,
  reasonValue: "",
  onReasonChange: undefined,
  reasonPlaceholder: "",
};

export default function UserModerationPanel({ onStatusChange }) {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [participationFilter, setParticipationFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [processing, setProcessing] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [statusSummary, setStatusSummary] = useState({
    totalMembers: 0,
    banned: 0,
    suspended: 0,
    trusted: 0,
  });
  const [suspensionForm, setSuspensionForm] = useState({
    userId: null,
    preset: 24,
    customUntil: "",
    moderationReason: "",
  });
  const {
    isOpen: isSuspendModalOpen,
    onOpen: onOpenSuspendModal,
    onClose: onCloseSuspendModal,
  } = useDisclosure();
  const {
    isOpen: isConfirmModalOpen,
    onOpen: onOpenConfirmModal,
    onClose: onCloseConfirmModal,
  } = useDisclosure();
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    userId: null,
    action: "",
    title: "",
    message: "",
    danger: false,
    payload: {},
    collectReason: false,
    reasonValue: "",
    reasonPlaceholder: "",
  });

  const pageSize = 10;

  const commitSearch = useCallback(
    (raw) => {
      const next = raw.trim();
      setPage((prev) => (prev === 1 ? prev : 1));
      setSearch((prev) => (prev === next ? prev : next));
    },
    [setPage, setSearch]
  );

  // Auto-apply search while typing (debounced) so users don't need to click the icon.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      commitSearch(searchInput);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchInput, commitSearch]);

  // Hydrate search from query params (?q=)
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const qp = params.get("q") || params.get("search") || "";
    if (qp) {
      setSearchInput(qp);
      setSearch(qp);
      setPage(1);
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await communityAPI.admin.users.getAll({
          page,
          page_size: pageSize,
          search: search || undefined,
        });

        if (cancelled) {
          return;
        }

        const payload = response.data;
        const records = extractResults(payload);
        const filteredRecords = filterModeratedMembers(records);
        setUsers(filteredRecords);
        setTotal(
          typeof payload?.count === "number" ? payload.count : filteredRecords.length
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load user statuses:", err);
          message.error("Unable to load user statuses right now.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, refreshKey]);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const summaryPageSize = 200;
        const firstResponse = await communityAPI.admin.users.getAll({
          page: 1,
          page_size: summaryPageSize,
          search: search || undefined,
        });

        if (cancelled) {
          return;
        }

        const firstPayload = firstResponse?.data || {};
        const totalCount = Number(firstPayload?.count) || 0;
        const totalPages = Math.max(1, Math.ceil(totalCount / summaryPageSize));
        const mergedRecords = [...filterModeratedMembers(extractResults(firstPayload))];

        for (let nextPage = 2; nextPage <= totalPages; nextPage += 1) {
          const pageResponse = await communityAPI.admin.users.getAll({
            page: nextPage,
            page_size: summaryPageSize,
            search: search || undefined,
          });

          if (cancelled) {
            return;
          }

          mergedRecords.push(
            ...filterModeratedMembers(extractResults(pageResponse?.data || {}))
          );
        }

        if (!cancelled) {
          setStatusSummary(buildSummary(mergedRecords));
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load moderation summary:", err);
          message.error("Unable to load member insights right now.");
          setStatusSummary({ totalMembers: 0, banned: 0, suspended: 0, trusted: 0 });
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [search, refreshKey]);

  const resetSuspensionForm = () => {
    setSuspensionForm({ userId: null, preset: 24, customUntil: "", moderationReason: "" });
    onCloseSuspendModal();
  };

  const triggerRefresh = () => setRefreshKey((prev) => prev + 1);

  const performAction = async (userId, endpoint, payload = {}) => {
    setProcessing((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await communityAPI.admin.users.performAction(
        userId,
        endpoint,
        payload
      );
      const detail = response?.data?.detail || "User status updated.";
      message.success(detail);
      triggerRefresh();
      if (typeof onStatusChange === "function") {
        onStatusChange();
      }
    } catch (err) {
      console.error(`Failed to ${endpoint} user:`, err);
      const detail = err?.response?.data?.detail || "Unable to update user status.";
      message.error(detail);
    } finally {
      setProcessing((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      if (suspensionForm.userId === userId && endpoint !== "suspend") {
        resetSuspensionForm();
      }
      onCloseConfirmModal();
    }
  };

  const handleSuspendConfirm = async () => {
    const { userId, preset, customUntil } = suspensionForm;
    if (!userId) {
      return;
    }

    let payload;
    const reason = (suspensionForm.moderationReason || "").trim();

    if (preset === "custom") {
      if (!customUntil) {
        message.warning("Select a suspension end date.");
        return;
      }
      const date = new Date(customUntil);
      if (Number.isNaN(date.getTime())) {
        message.warning("Provide a valid suspension date.");
        return;
      }
      payload = { suspended_until: date.toISOString() };
    } else {
      const hours = Number(preset);
      if (!Number.isFinite(hours) || hours <= 0) {
        message.warning("Select a valid suspension duration.");
        return;
      }
      payload = { duration_hours: hours };
    }

    if (reason) {
      payload.moderation_reason = reason;
    }

    await performAction(userId, "suspend", payload);
    resetSuspensionForm();
  };

  const handleSuspendClick = (userId) => {
    setSuspensionForm({ userId, preset: 24, customUntil: "", moderationReason: "" });
    onOpenSuspendModal();
  };

  const confirmAndPerform = (userId, action, config) => {
    setConfirmModalConfig({
      action,
      ...config,
      userId,
      reasonValue: "",
      payload: { ...(config.payload || {}) },
    });
    onOpenConfirmModal();
  };

  const handleConfirmModalSubmit = async () => {
    const { userId, action, payload, reasonValue } = confirmModalConfig;
    if (!userId || !action) {
      return;
    }

    const nextPayload = { ...(payload || {}) };
    const reason = (reasonValue || "").trim();
    if (reason) {
      nextPayload.moderation_reason = reason;
    }

    await performAction(userId, action, nextPayload);
  };

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const status = user.status || {};

        if (statusFilter === "banned" && !status.is_banned) return false;
        if (statusFilter === "suspended" && !status.currently_suspended) return false;
        if (statusFilter === "trusted" && !status.is_trusted) return false;

        if (
          participationFilter === "restricted" &&
          status.can_post !== false &&
          status.can_comment !== false
        ) {
          return false;
        }

        return true;
      }),
    [users, statusFilter, participationFilter]
  );

  const isProcessing = (userId) => Boolean(processing[userId]);

  const applySearch = () => {
    commitSearch(searchInput);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applySearch();
    }
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setParticipationFilter("all");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const paginationTotal = summaryLoading ? total : statusSummary.totalMembers;

  return (
    <section className="space-y-6">
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={onCloseConfirmModal}
        onConfirm={handleConfirmModalSubmit}
        isLoading={isProcessing(confirmModalConfig.userId)}
        onReasonChange={(value) =>
          setConfirmModalConfig((prev) => ({ ...prev, reasonValue: value }))
        }
        {...confirmModalConfig}
      />
      <Modal
        open={isSuspendModalOpen}
        onCancel={resetSuspensionForm}
        title={
          <div className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-amber-500" />
            <span className="text-lg font-semibold">Schedule Suspension</span>
          </div>
        }
        footer={[
          <button key="back" onClick={resetSuspensionForm} disabled={isProcessing(suspensionForm.userId)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            Cancel
          </button>,
          <button
            key="submit"
            onClick={handleSuspendConfirm}
            disabled={isProcessing(suspensionForm.userId)}
            className="ml-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700"
          >
            {isProcessing(suspensionForm.userId) ? "Suspending..." : "Confirm Suspension"}
          </button>,
        ]}
      >
        <div className="space-y-4 p-4">
          <select
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            value={suspensionForm.preset}
            onChange={(event) =>
              setSuspensionForm((prev) => ({
                ...prev,
                preset: event.target.value,
              }))
            }
          >
            {SUSPEND_PRESETS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {suspensionForm.preset === "custom" && (
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              value={suspensionForm.customUntil}
              onChange={(event) =>
                setSuspensionForm((prev) => ({
                  ...prev,
                  customUntil: event.target.value,
                }))
              }
            />
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Moderation Reason (optional)
            </label>
            <TextArea
              rows={3}
              maxLength={500}
              value={suspensionForm.moderationReason}
              placeholder="Provide context for the user notification"
              onChange={(event) =>
                setSuspensionForm((prev) => ({
                  ...prev,
                  moderationReason: event.target.value,
                }))
              }
            />
          </div>
        </div>
      </Modal>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h3 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
            Member Access Control
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Review and manage access, suspensions, and trust for community members.
          </p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <div className="w-full sm:w-72">
            <SearchInput
              placeholder="Search by username or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={applySearch}
              onKeyDown={handleSearchKeyDown}
              allowClear
              loading={loading}
              enterButton={<Search className="h-4 w-4" />}
              className="w-full bg-primary/5 dark:bg-gray-800 [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-input-wrapper]:dark:bg-gray-800 [&_.ant-input-group-addon]:dark:bg-gray-700 [&_.ant-input-group-addon]:dark:border-gray-700 [&_.ant-btn]:dark:bg-primary [&_.ant-btn]:dark:border-primary [&_.ant-btn]:dark:text-white [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700 [&_.ant-input-affix-wrapper]:dark:text-white [&_.ant-input-clear-icon]:dark:text-gray-400"
              size="large"
            />
          </div>
          <button
            type="button"
            onClick={triggerRefresh}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <span> <RefreshCw className="h-4 w-4" /></span>
          </button>
        </div>
      </header>

    
 <div className="mb-4 space-y-3">
          <h4 className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Member insights
          </h4>

          {summaryLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse rounded-2xl bg-slate-50/90 p-4 shadow-sm ring-1 ring-slate-100/80 dark:bg-gray-900/80 dark:ring-gray-800"
                >
                  <div className="h-3 w-20 rounded-full bg-slate-200/80 dark:bg-gray-700" />
                  <div className="mt-3 h-6 w-16 rounded-full bg-slate-200/90 dark:bg-gray-700" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl bg-gradient-to-br from-sky-50 to-cyan-100/60 p-4 shadow-sm ring-1 ring-sky-100/80 dark:from-sky-950/70 dark:to-cyan-900/40 dark:ring-sky-900/60">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-700/90 dark:text-sky-200">
                      Total Members
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-sky-700 dark:text-sky-100">
                      {statusSummary.totalMembers}
                    </div>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-600 dark:bg-sky-900/70 dark:text-sky-200">
                    <Users className="h-4 w-4" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-rose-100/60 p-4 shadow-sm ring-1 ring-rose-100/80 dark:from-rose-950/70 dark:to-rose-900/40 dark:ring-rose-900/60">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-600/90 dark:text-rose-200">
                      Banned
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-rose-700 dark:text-rose-100">
                      {statusSummary.banned}
                    </div>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-500 dark:bg-rose-900/70 dark:text-rose-200">
                    <ShieldOff className="h-4 w-4" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100/70 p-4 shadow-sm ring-1 ring-amber-100/80 dark:from-amber-950/70 dark:to-amber-900/40 dark:ring-amber-900/60">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700/90 dark:text-amber-200">
                      Suspended
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-amber-700 dark:text-amber-100">
                      {statusSummary.suspended}
                    </div>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/70 dark:text-amber-200">
                    <Slash className="h-4 w-4" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/70 p-4 shadow-sm ring-1 ring-emerald-100/80 dark:from-emerald-950/70 dark:to-emerald-900/40 dark:ring-emerald-900/60">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700/90 dark:text-emerald-200">
                      Trusted
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-emerald-700 dark:text-emerald-100">
                      {statusSummary.trusted}
                    </div>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/70 dark:text-emerald-200">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

  <div className="rounded-2xl border border-gray-100 bg-slate-50/80 p-3 shadow-sm backdrop-blur-sm dark:border-gray-800/80 dark:bg-gray-900/80">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Filters
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Status</span>
              <select
                className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="banned">Banned</option>
                <option value="suspended">Suspended</option>
                <option value="trusted">Trusted</option>
              </select>
            </div>
            <div className="hidden h-5 w-px bg-gray-200 dark:bg-gray-700 sm:block" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Participation</span>
              <select
                className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                value={participationFilter}
                onChange={(event) => setParticipationFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClearFilters}
            className="ml-auto text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline dark:text-gray-400 dark:hover:text-gray-100"
          >
            Clear filters
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/70">
       
        <div className="mt-4 border-t border-dashed border-gray-100 pt-4 dark:border-gray-800/80">
          {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="animate-pulse rounded-2xl border border-gray-100 bg-slate-50/90 p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900/60"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 rounded-full bg-slate-200/90 dark:bg-gray-700" />
                    <div className="h-3 w-52 rounded-full bg-slate-200/80 dark:bg-gray-700/80" />
                  </div>
                  <div className="hidden w-48 space-y-2 md:block">
                    <div className="h-3 w-full rounded-full bg-slate-200/80 dark:bg-gray-700/80" />
                    <div className="h-3 w-3/4 rounded-full bg-slate-200/80 dark:bg-gray-700/80" />
                  </div>
                  <div className="h-8 w-8 rounded-full bg-slate-200/80 dark:bg-gray-700/80" />
                </div>
              </div>
            ))}
          </div>
          ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-gray-300">
              <Search className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                No members match your filters
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Try adjusting your filters or clearing them to see all members.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="mt-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-600 dark:hover:bg-gray-800"
            >
              Clear filters
            </button>
          </div>
          ) : (
          <div>
            <table className="w-full table-fixed border-separate border-spacing-y-2 text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="whitespace-nowrap border-b border-gray-200/70 px-4 py-3 dark:border-gray-800">
                    Member
                  </th>
                  <th className="hidden whitespace-nowrap border-b border-gray-200/70 px-4 py-3 dark:border-gray-800 md:table-cell">
                    Account
                  </th>
                  <th className="whitespace-nowrap border-b border-gray-200/70 px-4 py-3 dark:border-gray-800">
                    Status
                  </th>
                  <th className="hidden whitespace-nowrap border-b border-gray-200/70 px-4 py-3 dark:border-gray-800 md:table-cell">
                    Suspension
                  </th>
                  <th className="hidden whitespace-nowrap border-b border-gray-200/70 px-4 py-3 dark:border-gray-800 lg:table-cell">
                    Joined
                  </th>
                  <th className="hidden whitespace-nowrap border-b border-gray-200/70 px-4 py-3 dark:border-gray-800 lg:table-cell">
                    Last Seen
                  </th>
                  <th className="whitespace-nowrap border-b border-gray-200/70 px-4 py-3 text-right dark:border-gray-800">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const status = user.status || {};
                  const busy = isProcessing(user.id);
                  const currentlySuspended = Boolean(status.currently_suspended);
                  const suspendedUntil = formatDateTime(status.suspended_until);
                  const isBanned = Boolean(status.is_banned);
                  const canPost = status.can_post !== false;
                  const canComment = status.can_comment !== false;
                  const isTrusted = Boolean(status.is_trusted);

                  const actionItems = [
                    {
                      key: "suspend",
                      label: renderActionLabel("suspend", "Suspend User"),
                      icon: <Slash className="mr-2 h-4 w-4" />,
                      onClick: () => handleSuspendClick(user.id),
                      hidden: currentlySuspended || isBanned,
                    },
                    {
                      key: "unsuspend",
                      label: "Unsuspend User",
                      icon: <Unlock className="mr-2 h-4 w-4" />,
                      onClick: () =>
                        confirmAndPerform(user.id, "unsuspend", {
                          title: "Confirm Unsuspend",
                          message: `Are you sure you want to lift the suspension for ${user.full_name}?`,
                          confirmText: "Unsuspend",
                          collectReason: true,
                          reasonPlaceholder: "Optional reason shown in the notification",
                        }),
                      hidden: !currentlySuspended,
                    },
                    {
                      key: "ban",
                      label: renderActionLabel("ban", "Ban User"),
                      icon: <ShieldOff className="mr-2 h-4 w-4" />,
                      onClick: () =>
                        confirmAndPerform(user.id, "ban", {
                          title: "Confirm Ban",
                          message: `Banning ${user.full_name} will permanently restrict their access. Are you sure?`,
                          confirmText: "Ban User",
                          danger: true,
                          collectReason: true,
                          reasonPlaceholder: "Optional reason shown in the notification",
                        }),
                      hidden: isBanned,
                    },
                    {
                      key: "unban",
                      label: "Unban User",
                      icon: <ShieldCheck className="mr-2 h-4 w-4" />,
                      onClick: () =>
                        confirmAndPerform(user.id, "unban", {
                          title: "Confirm Unban",
                          message: `Are you sure you want to unban ${user.full_name}?`,
                          confirmText: "Unban User",
                          collectReason: true,
                          reasonPlaceholder: "Optional reason shown in the notification",
                        }),
                      hidden: !isBanned,
                    },
                    {
                      key: "toggle-posting",
                      label: renderActionLabel(
                        "toggle-posting",
                        canPost ? "Disable Posting" : "Enable Posting",
                        { enabled: canPost }
                      ),
                      icon: canPost ? (
                        <UserX className="mr-2 h-4 w-4" />
                      ) : (
                        <UserCheck className="mr-2 h-4 w-4" />
                      ),
                      onClick: () =>
                        confirmAndPerform(user.id, "restrict-posting", {
                          title: `Confirm ${canPost ? "Disable" : "Enable"} Posting`,
                          message: `Are you sure you want to ${canPost ? "disable" : "enable"} posting for ${user.full_name}?`,
                          confirmText: canPost ? "Disable" : "Enable",
                          payload: { can_post: !canPost },
                          collectReason: true,
                          reasonPlaceholder: "Optional reason shown in the notification",
                        }),
                    },
                    {
                      key: "toggle-commenting",
                      label: renderActionLabel(
                        "toggle-commenting",
                        canComment ? "Disable Commenting" : "Enable Commenting",
                        { enabled: canComment }
                      ),
                      icon: canComment ? (
                        <UserX className="mr-2 h-4 w-4" />
                      ) : (
                        <UserCheck className="mr-2 h-4 w-4" />
                      ),
                      onClick: () =>
                        confirmAndPerform(user.id, "restrict-commenting", {
                          title: `Confirm ${canComment ? "Disable" : "Enable"} Commenting`,
                          message: `Are you sure you want to ${canComment ? "disable" : "enable"} commenting for ${user.full_name}?`,
                          confirmText: canComment ? "Disable" : "Enable",
                          payload: { can_comment: !canComment },
                          collectReason: true,
                          reasonPlaceholder: "Optional reason shown in the notification",
                        }),
                    },
                    {
                      key: "toggle-trust",
                      label: renderActionLabel(
                        "toggle-trust",
                        isTrusted ? "Unmark Trusted" : "Mark as Trusted",
                        { trusted: isTrusted }
                      ),
                      icon: isTrusted ? (
                        <ShieldOff className="mr-2 h-4 w-4" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      ),
                      onClick: () =>
                        confirmAndPerform(user.id, isTrusted ? "untrust" : "trust", {
                          title: `Confirm ${isTrusted ? "Untrust" : "Trust"}`,
                          message: `Are you sure you want to ${isTrusted ? "unmark as trusted" : "mark as trusted"} ${user.full_name}?`,
                          confirmText: isTrusted ? "Unmark" : "Mark Trusted",
                          collectReason: true,
                          reasonPlaceholder: "Optional reason shown in the notification",
                        }),
                    },
                  ];

                  return (
                    <tr
                      key={user.id}
                      className="group rounded-2xl bg-white/90 shadow-sm ring-1 ring-gray-100/80 transition hover:-translate-y-[1px] hover:bg-white hover:shadow-md hover:ring-primary/10 dark:bg-gray-900/80 dark:ring-gray-800"
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="space-y-1">
                          <Link 
                            to={`/profile/${user.username}`}
                            className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-primary transition-colors flex items-center gap-1.5"
                          >
                            {user.full_name}
                            {isTrusted && (
                              <img
                                src={verifiedBadge}
                                alt="Verified"
                                className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                              />
                            )}
                          </Link>
                          <div className="text-xs text-gray-600 dark:text-gray-400 md:hidden">
                            @{user.username} · {user.email}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 md:hidden">
                            {isBanned && (
                              <span
                                className={`${badgeBaseClass} bg-rose-500/15 text-rose-600 dark:text-rose-300`}
                              >
                                Banned
                              </span>
                            )}
                            {currentlySuspended && (
                              <span
                                className={`${badgeBaseClass} bg-amber-500/15 text-amber-600 dark:text-amber-200`}
                              >
                                Suspended
                              </span>
                            )}
                            {!canPost && (
                              <span
                                className={`${badgeBaseClass} bg-slate-500/15 text-slate-600 dark:text-slate-200`}
                              >
                                Posting Disabled
                              </span>
                            )}
                            {!canComment && (
                              <span
                                className={`${badgeBaseClass} bg-slate-500/15 text-slate-600 dark:text-slate-200`}
                              >
                                Commenting Disabled
                              </span>
                            )}
                            {isTrusted && (
                              <span
                                className={`${badgeBaseClass} bg-emerald-500/15 text-emerald-600 dark:text-emerald-200`}
                              >
                                Trusted
                              </span>
                            )}
                          </div>
                          <div className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-500 lg:hidden">
                            <div>Joined: {formatDateTime(user.date_joined)}</div>
                            <div>Last seen: {formatDateTime(user.last_login)}</div>
                            {currentlySuspended && (
                              <div className="font-semibold text-amber-600 dark:text-amber-200">
                                Suspended until: {suspendedUntil}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="hidden px-4 py-4 align-top text-xs text-gray-600 dark:text-gray-400 md:table-cell">
                        <div className="space-y-1">
                          <Link 
                            to={`/profile/${user.username}`}
                            className="text-sm text-gray-700 dark:text-gray-200 hover:text-primary transition-colors"
                          >
                            @{user.username}
                          </Link>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="hidden flex-wrap gap-2 md:flex">
                          {isBanned && (
                            <span
                              className={`${badgeBaseClass} bg-rose-500/15 text-rose-600 dark:text-rose-300`}
                            >
                              Banned
                            </span>
                          )}
                          {currentlySuspended && (
                            <span
                              className={`${badgeBaseClass} bg-amber-500/15 text-amber-600 dark:text-amber-200`}
                            >
                              Suspended
                            </span>
                          )}
                          {!canPost && (
                            <span
                              className={`${badgeBaseClass} bg-slate-500/15 text-slate-600 dark:text-slate-200`}
                            >
                              Posting Disabled
                            </span>
                          )}
                          {!canComment && (
                            <span
                              className={`${badgeBaseClass} bg-slate-500/15 text-slate-600 dark:text-slate-200`}
                            >
                              Commenting Disabled
                            </span>
                          )}
                          {isTrusted && (
                            <span
                              className={`${badgeBaseClass} bg-emerald-500/15 text-emerald-600 dark:text-emerald-200`}
                            >
                              Trusted
                            </span>
                          )}
                          {!isBanned &&
                            !currentlySuspended &&
                            canPost &&
                            canComment &&
                            !isTrusted && (
                              <span className="text-xs text-gray-500 dark:text-gray-500">
                                —
                              </span>
                            )}
                        </div>
                      </td>

                      <td className="hidden px-4 py-4 align-top text-sm text-gray-600 dark:text-gray-400 md:table-cell">
                        {currentlySuspended ? (
                          <span className="font-semibold text-amber-600 dark:text-amber-200">
                            Until {suspendedUntil}
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-500">—</span>
                        )}
                      </td>

                      <td className="hidden whitespace-nowrap px-4 py-4 align-top text-sm text-gray-600 dark:text-gray-400 lg:table-cell">
                        {formatDateTime(user.date_joined)}
                      </td>
                      <td className="hidden whitespace-nowrap px-4 py-4 align-top text-sm text-gray-600 dark:text-gray-400 lg:table-cell">
                        {formatDateTime(user.last_login)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-4 align-top text-right">
                        <Dropdown
                          menu={{ items: actionItems.filter((item) => !item.hidden) }}
                          trigger={["click"]}
                          disabled={busy}
                        >
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-60 dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                            aria-label={`Manage moderation for ${user.full_name}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      <div className="flex justify-center rounded-xl bg-white/80 py-2 dark:bg-gray-900/60">
        <Pagination
          size="small"
          current={page}
          pageSize={pageSize}
          total={paginationTotal}
          onChange={(nextPage) => setPage(nextPage)}
          showSizeChanger={false}
        />
      </div>
    </section>
  );
}

UserModerationPanel.propTypes = {
  onStatusChange: PropTypes.func,
};
